import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { PLAYER_POC_ROUTES } from "constants/routes";

import { useDiscoContent } from "hooks/useDiscoContent";

import { ImmersiveReaderButton } from "components/ImmersiveReaderButton/ImmersiveReaderButton";
import { InfoMessage } from "components/InfoMessage/InfoMessage";
import { LoadingSpinner } from "components/LoadingSpinner";

import {
  StyleHeader,
  StyledCard,
  StyledTopicListItem,
} from "pages/StudentEnvironment/StudentDashboard/subpages/WritingSubjectDetailsPage/components/ChapterList/ChapterList.styled";
import { Box, CssButton } from "styles/helpers/layout";

export const ChapterOverviewPage: React.FC = observer(() => {
  const { chapterId, packageId } = useParams<{ packageId: string; chapterId: string }>();
  const {
    chapterOverview,
    fetchChapterOverview,
    setChapterOverview,
    loading: isLoading,
  } = useDiscoContent();

  const navigate = useNavigate();
  const IR_TARGET_SELECTOR = `subject-immersive-reader-${chapterId}`;

  useEffect(() => {
    if (packageId && chapterId) {
      fetchChapterOverview(packageId, chapterId);
    }

    // Reset chapter data in state upon leaving page
    return () => {
      setChapterOverview();
    };
  }, [packageId, chapterId, fetchChapterOverview]);

  return (
    <Box padding="20px">
      <h1>Player POC Page</h1>
      <p>This is a proof of concept page to show the list of blocks within an Edify chapter.</p>

      {isLoading && <LoadingSpinner />}

      {/* -- Not found message -- */}
      {!isLoading && !chapterOverview && (
        <InfoMessage dataCy="no-content-message" icon="fas fa-debug" title="Oops!">
          No content found for this chapter
        </InfoMessage>
      )}

      {/* -- Chapter overview contents -- */}
      {!isLoading && packageId && chapterId && chapterOverview && (
        <Box paddingTop="2rem">
          <StyledCard>
            <StyleHeader
              data-cy="edify-chapter-heading"
              alignItems="center"
              columnGap={1}
              justifyContent="space-between"
            >
              {chapterOverview.title}
              <ImmersiveReaderButton targetIndex={0} targetSelector={`.${IR_TARGET_SELECTOR}`} />
            </StyleHeader>
            <div>
              {chapterOverview.blocks.map((subchapter) => (
                <StyledTopicListItem
                  key={subchapter.id}
                  dataCy="edify-subchapter-list-item"
                  href={buildUrlWithPathParams(PLAYER_POC_ROUTES.SUBCHAPTER_THEORY, {
                    packageId,
                    chapterId,
                    subchapterId: subchapter.id,
                  })}
                  title={subchapter.title}
                />
              ))}
            </div>
          </StyledCard>

          {/* @ts-ignore */}
          {chapterOverview.hasDrilling && (
            <CssButton
              dataCy="edify-chapter-extra-practice-button"
              variant="primary"
              onClick={() =>
                navigate(
                  buildUrlWithPathParams(PLAYER_POC_ROUTES.CHAPTER_EXTRA_PRACTISING, {
                    packageId,
                    chapterId,
                  }),
                )
              }
            >
              Go to extra practice
            </CssButton>
          )}
        </Box>
      )}
    </Box>
  );
});
